import React, { useState, useCallback } from 'react';
import { message } from 'antd';
import { Space, SpaceType } from '../../../types/space';
import spaceService from '../../../service/spaceService';
import type { TreeNodeData } from '@ms/shared-components';
import { getMenuItemsBySpaceType } from '../configs/treeConfig';

export const useSpaceTreeData = () => {
  const [treeData, setTreeData] = useState<TreeNodeData[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [loadedKeys, setLoadedKeys] = useState<Set<string>>(new Set());

  // 将Space转换为TreeNodeData
  const spaceToTreeNode = useCallback((space: Space, hasChildren: boolean = false): TreeNodeData => {
    return {
      key: space.id,
      title: `${space.name} (${space.code})`,
      children: hasChildren ? [] : undefined, // 如果有子节点，设置为空数组等待加载
      isLeaf: space.type === SpaceType.SPACE, // SPACE类型为叶子节点，不能再有子节点
      space: space,
      // 根据空间类型和是否来自酒店动态设置菜单项
      menuItems: getMenuItemsBySpaceType(space)
    };
  }, []);

  // 加载建筑列表（初始加载）
  const loadBuildings = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);


      const response = await spaceService.getBuildings();
      console.log('API响应:', response);

      if (response.success && response.data) {
        const buildings = response.data as Space[];
        console.log('获取到的建筑数据:', buildings);
        console.log('建筑数量:', buildings.length);

        // 转换为树节点，所有建筑都可能有子节点
        const buildingNodes = buildings.map(building =>
          spaceToTreeNode(building, true)
        );

        console.log('转换后的树节点:', buildingNodes);
        console.log('树节点数量:', buildingNodes.length);

        setTreeData(buildingNodes);
        setLoadedKeys(new Set(['root'])); // 标记根级已加载
      } else {
        console.error('加载建筑失败:', response.error);
        message.error(response.error || '加载建筑失败');
        setTreeData([]);
      }
    } catch (error) {
      console.error('加载建筑异常:', error);
      message.error('加载建筑失败');
      setTreeData([]);
    } finally {
      setLoading(false);
    }
  }, [spaceToTreeNode]);

  // 加载指定节点的子节点
  const loadChildren = useCallback(async (parentId: string): Promise<TreeNodeData[]> => {
    try {
      const response = await spaceService.getChildren(parentId);

      if (response.success && response.data) {
        const children = response.data as Space[];

        // 转换为树节点
        const childNodes = children.map(child => {
          const hasGrandChildren = child.type !== SpaceType.SPACE; // 非SPACE类型可能有子节点
          return spaceToTreeNode(child, hasGrandChildren);
        });

        setLoadedKeys(prev => new Set(Array.from(prev).concat(parentId))); // 标记该节点已加载
        return childNodes;
      } else {
        console.error('加载子节点失败:', response.error);
        message.error(response.error || '加载子节点失败');
        return [];
      }
    } catch (error) {
      console.error('加载子节点异常:', error);
      message.error('加载子节点失败');
      return [];
    }
  }, [spaceToTreeNode]);

  // 处理节点展开
  const handleNodeExpand = useCallback(async (expandedKeys: React.Key[], info: any): Promise<void> => {
    setExpandedKeys(expandedKeys);

    // 检查是否有新展开的节点需要加载子节点
    if (info.expanded && info.node) {
      const nodeKey = info.node.key as string;

      // 如果该节点未加载过子节点，则加载
      if (!loadedKeys.has(nodeKey)) {
        const children = await loadChildren(nodeKey);

        // 更新树数据，将子节点添加到对应的父节点下
        const updateTreeData = (nodes: TreeNodeData[]): TreeNodeData[] => {
          return nodes.map(node => {
            if (node.key === nodeKey) {
              return {
                ...node,
                children: children
              };
            }
            if (node.children) {
              return {
                ...node,
                children: updateTreeData(node.children)
              };
            }
            return node;
          });
        };

        setTreeData(prevTreeData => updateTreeData(prevTreeData));
      }
    }
  }, [loadedKeys, loadChildren]);

  // 重新加载指定父节点的子节点
  const reloadNodeChildren = useCallback(async (parentId: string): Promise<void> => {
    try {


      // 直接调用API获取子节点数据
      const response = await spaceService.getChildren(parentId);
      console.log('子节点API响应:', response);

      if (response.success && response.data) {
        const children = response.data as Space[];
        console.log('获取到的子节点数据:', children);
        console.log('子节点数量:', children.length);

        // 转换为树节点
        const childNodes = children.map(child => {
          const hasGrandChildren = child.type !== SpaceType.SPACE; // 非SPACE类型可能有子节点
          return spaceToTreeNode(child, hasGrandChildren);
        });

        console.log('转换后的子节点:', childNodes);

        // 更新树数据，将新的子节点替换到对应的父节点下
        setTreeData(prevTreeData => {
          console.log('更新前的树数据:', prevTreeData);

          const updateTreeData = (nodes: TreeNodeData[]): TreeNodeData[] => {
            return nodes.map(node => {
              if (node.key === parentId) {
                console.log('找到父节点，更新子节点:', node.key, '新子节点数量:', childNodes.length);
                return {
                  ...node,
                  children: childNodes
                };
              }
              if (node.children) {
                return {
                  ...node,
                  children: updateTreeData(node.children)
                };
              }
              return node;
            });
          };

          const updatedTreeData = updateTreeData(prevTreeData);
          console.log('更新后的树数据:', updatedTreeData);
          return updatedTreeData;
        });

        // 标记该节点已加载
        setLoadedKeys(prev => new Set(Array.from(prev).concat(parentId)));

        // 确保父节点保持展开状态
        setExpandedKeys(prev => {
          const newKeys = [...prev];
          if (!newKeys.includes(parentId)) {
            newKeys.push(parentId);
          }
          console.log('更新展开状态:', newKeys);
          return newKeys;
        });
      } else {
        console.error('加载子节点失败:', response.error);
      }

    } catch (error) {
      console.error('重新加载节点子节点失败:', error);
    }
  }, [spaceToTreeNode]);

  // 重新加载整个树（刷新）
  const reloadTree = useCallback(async (): Promise<void> => {
    setLoadedKeys(new Set()); // 清空已加载标记
    setExpandedKeys([]); // 清空展开状态
    await loadBuildings();
  }, [loadBuildings]);

  return {
    treeData,
    loading,
    expandedKeys,
    loadBuildings,
    loadChildren,
    handleNodeExpand,
    reloadTree,
    reloadNodeChildren
  };
};